import { useDispatch } from 'react-redux';
import {
	Col,
	Alert,
	Spinner,
	CardBody,
	Card,
	CardHeader,
	Row,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import AddEditPowerGenerationHourMeterModal from './Add_Edit/AddEditPowerGenerationHourMeter';
import DeletePowerGenerationHourMeterModal from './Delete/DeletePowerGenerationHourMeter';
import { getPowerGenerationHourMeterList } from '../../../slices/powerGenerationHourMeter/thunk';
import { getPowerGenerationHourMeterList as getPowerGenerationHourMeterListApi } from '../../../helpers/establishment/backend_helper';
import AddEditPowerGenerationConsumptionModal from './Add_Edit/AddEditPowerGenerationConsumption';
import { getFuelTankList } from '../../../slices/thunks';

const PowerGenerationHourMeterList = ({ establishment_id }) => {
	const dispatch = useDispatch();
	const history = useNavigate();
	const [refetch, setRefetch] = useState(false);
	const [processedList, setProcessedList] = useState([]);

	const toggleRefetch = () => {
		setRefetch(!refetch);
	};

	const selectorPowerGenerationHourMeter = createSelector(
		state => state.PowerGenerationHourMeter,
		data => ({
			powerGenerationHourMeterList: data.itemPowerGenerationHourMeterList,
			isLoadingList: data.isLoadingList,
			isPowerGenerationHourMeterSuccess:
				data.isPowerGenerationHourMeterSuccess,
		})
	);
	const selectorFuelTank = createSelector(
		state => state.FuelTank,
		data => ({
			fuelTankList: data.fuelTankList,
		})
	);

	const { powerGenerationHourMeterList, isLoadingList } = useSelector(
		selectorPowerGenerationHourMeter
	);
	const { fuelTankList } = useSelector(selectorFuelTank);

	useEffect(() => {
		if (establishment_id) {
			dispatch(
				getPowerGenerationHourMeterList(
					establishment_id !== 'all' ? { establishment_id } : undefined
				)
			);
			dispatch(
				getFuelTankList(
					establishment_id !== 'all' ? { establishment_id } : undefined
				)
			);
		}
	}, [establishment_id, dispatch, refetch]);

	useEffect(() => {
		if (
			!powerGenerationHourMeterList ||
			powerGenerationHourMeterList.length === 0 ||
			!fuelTankList
		) {
			setProcessedList([]);
			return;
		}

		// Agrupar registros por fuente de origen
		const recordsBySource = powerGenerationHourMeterList.reduce(
			(acc, record) => {
				const sourceId =
					record.power_generation_source?._id ||
					record.consumption_source?._id;
				if (sourceId) {
					if (!acc[sourceId]) {
						acc[sourceId] = [];
					}
					acc[sourceId].push(record);
				}
				return acc;
			},
			{}
		);

		const deletableRecordIds = new Set();

		// Para cada fuente, encontrar el último horómetro y el último consumo
		for (const sourceId in recordsBySource) {
			const group = recordsBySource[sourceId];
			const hourMeterRecords = group.filter(r => r.hour_meter);
			const consumptionRecords = group.filter(r => r.consumption);

			const findLastRecord = records =>
				records.reduce(
					(latest, current) =>
						new Date(current.register_date) >
						new Date(latest.register_date)
							? current
							: latest,
					records[0]
				);

			if (hourMeterRecords.length > 0) {
				const lastHourMeterRecord = findLastRecord(hourMeterRecords);
				deletableRecordIds.add(lastHourMeterRecord._id);
			}

			if (consumptionRecords.length > 0) {
				const lastConsumptionRecord = findLastRecord(consumptionRecords);
				deletableRecordIds.add(lastConsumptionRecord._id);
			}
		}

		const newList = powerGenerationHourMeterList.map(item => {
			const isDeletable = deletableRecordIds.has(item._id);
			const isConsumptionRecord = !!item.consumption;
			const tankExists = fuelTankList.some(
				tank => tank._id === item.fuel_tank?._id
			);

			// La regla del usuario: para horómetros, solo importa si es el último.
			// Para consumos, importa si es el último Y si el estanque existe.
			const canBeDeleted =
				isDeletable && (!isConsumptionRecord || tankExists);

			return { ...item, canBeDeleted };
		});

		setProcessedList(newList);
	}, [powerGenerationHourMeterList, fuelTankList]);

	const [dataPowerGenerationHourMeter, setDataPowerGenerationHourMeter] =
		useState({});
	const [isEdit, setIsEdit] = useState(false);

	// Delete PowerGenerationHourMeter
	const [modal, setModal] = useState(false);
	const [modalConsumption, setModalConsumption] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	//Tab
	const [activeTab, setActiveTab] = useState('1');
	const toggleTab = tab => {
		if (activeTab !== tab) {
			setActiveTab(tab);
		}
	};

	function handleToggleModal() {
		setModal(!modal);
		toggleTab('1');
		// validation.resetForm();
	}

	function handleToggleModalDelete() {
		setModalDelete(!modalDelete);
	}

	function handleToggleModalConsumption() {
		setModalConsumption(!modalConsumption);
	}

	// Delete Data
	const handleClickDelete = powerGenerationHourMeter => {
		setDataPowerGenerationHourMeter(powerGenerationHourMeter);
		setModalDelete(true);
	};

	// Update Data
	const handleClickEdit = powerGenerationHourMeter => {
		setDataPowerGenerationHourMeter(powerGenerationHourMeter);
		handleToggleModal();
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'power_generation',
				columnDefType: 'display',

				size: 'auto',
				Cell: cellProps => {
					const record = cellProps.row.original;
					return (
						<React.Fragment>
							<div className='d-flex'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className='list-inline-item'>
											<Link
												to='#'
												className={`remove-item-btn ${
													!record.canBeDeleted ? 'disabled' : ''
												}`}
												onClick={e => {
													if (!record.canBeDeleted) {
														e.preventDefault();
														return;
													}
													const data = cellProps.row.original;
													handleClickDelete(data);
												}}
											>
												<i className='ri-delete-bin-line align-bottom me-2'></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{
				header: 'Fecha',
				accessorKey: 'register_date',
				dataType: 'date',
				width: 'auto',
				defaultSort: 'desc',
			},

			{
				header: 'Fuente',
				accessorKey: 'source_type_name',
				dataType: 'multi-select',
				size: 'auto',
				accessorFn: row => {
					let type = row.consumption_type?.code ?? null;
					let name = '-';
					if (type == 'CS') {
						name = row.consumption_source_type?.name;
					} else {
						name = row.power_generation_source?.name;
					}
					return name ? name : '-';
				},
				Cell: ({ row }) => {
					let type = row.original.consumption_type?.code ?? null;
					let name = '-';
					if (type == 'CS') {
						name = row.original.consumption_source_type?.name;
					} else {
						name = row.original.power_generation_source?.name;
					}
					return name ? name : '-';
				},
			},

			{
				header: 'Identificador',
				accessorKey: 'identity_name',
				dataType: 'multi-select',
				size: 'auto',
				accessorFn: row => {
					let type = row.consumption_type?.code ?? null;
					let name = '-';
					if (type == 'CS') {
						name = row.consumption_source?.name;
					} else {
						name = row.power_generation_source?.serial_number;
					}
					return name ? name : '-';
				},
				Cell: ({ row }) => {
					let type = row.original.consumption_type?.code ?? null;
					let name = '-';
					if (type == 'CS') {
						name = row.original.consumption_source?.name;
					} else {
						name = row.original.power_generation_source?.serial_number;
					}
					return name ? name : '-';
				},
			},
			{
				header: 'Consumo',
				accessorKey: 'consumption',
				width: 'auto',
				dataType: 'separator',
			},
			{
				header: 'Unidad',
				accessorKey: 'unit_measurement.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Saldo',
				accessorKey: 'balance',
				width: 'auto',
			},
			{
				header: 'Estanque',
				accessorKey: 'fuel_tank.name',
				width: 'auto',
			},
			{
				header: 'Tipo Combustible',
				accessorKey: 'fuel_type.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Kg de CO2 Nominal',
				accessorKey: 'nominal_CO2',
				width: 'auto',
				dataType: 'number',
			},
			{
				header: 'Horómetro Anterior',
				accessorKey: 'hour_meter_last',
				width: 'auto',
				dataType: 'separator',
			},
			{
				header: 'Horómetro',
				accessorKey: 'hour_meter',
				width: 'auto',
				dataType: 'separator',
			},
			{
				header: 'Horas de Uso',
				accessorKey: 'differences',
				width: 'auto',
				dataType: 'separator',
				summary: true,
			},
			// { header: 'Consumo Nominal', accessorKey: 'power_generation_source.nominal_consumption', width: 'auto', },
			// { header: 'Kg de CO2 Nominal', accessorKey: 'nominal_CO2', width: 'auto', dataType: 'number', },
			{
				header: 'Establecimiento',
				accessorKey: 'establishment.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Artefacto Naval',
				accessorKey: 'feeding_barges.name',
				width: 'auto',
				dataType: 'multi-select',
			},
			// {
			// 	header: 'Ciclo Productivo',
			// 	accessorKey: 'productive_cycle.code',
			// 	width: 'auto',
			// },
			{ header: 'Observación', accessorKey: 'observation' },
		],
		[establishment_id, handleClickDelete]
	);

	return (
		<React.Fragment>
			<style>
				{`
					.remove-item-btn.disabled {
						pointer-events: none;
						cursor: default;
					}
					.remove-item-btn.disabled .ri-delete-bin-line::before {
						color: #adb5bd !important;
					}
				`}
			</style>
			<Row>
				<Col lg={12}>
					<Card>
						{/* {establishment_id != 'all' && (
							<CardHeader className='border-0 pb-0'>
								<div className='d-flex align-items-end justify-content-end'>
									<div className='flex-shrink-0'>
										<div className='d-flex flex-wrap gap-2'>
											<button
												className='btn btn-label btn-primary'
												onClick={() => {
													setIsEdit(false);
													handleToggleModal();
												}}
											>
												<i className={`${Icons.plus} label-icon`}></i>{' '}
												Registrar Horómetro
											</button>
										</div>
									</div>
									<div
										className='flex-shrink-1'
										style={{ marginLeft: '5px' }}
									>
										<div className='d-flex flex-wrap gap-2'>
											<button
												className='btn btn-label btn-primary'
												onClick={() => {
													setIsEdit(false);
													handleToggleModalConsumption();
												}}
											>
												<i className={`${Icons.plus} label-icon`}></i>{' '}
												Registrar Consumo
											</button>
										</div>
									</div>
								</div>
							</CardHeader>
						)} */}
						<CardBody>
							<ODataGrid
								columns={columns}
								data={processedList || []}
								isLoadingData={isLoadingList}
								nameFile={'registro_consumo_combustible'}
								clearCacheFunc={() => {
									getPowerGenerationHourMeterListApi({
										skip: 0,
										limit: 1,
										clear_cache: true,
									}).then(res => {
										if (res) {
											toggleRefetch();
										}
									});
								}}
								customToolbarActions={establishment_id !== 'all' ? [
									{
										label: 'Registrar Horómetro',
										icon: Icons.plus,
										onClick: () => {
											setIsEdit(false);
											handleToggleModal();
										},
										title: 'Registrar Horómetro'
									},
									{
										label: 'Registrar Consumo',
										icon: Icons.plus,
										onClick: () => {
											setIsEdit(false);
											handleToggleModalConsumption();
										},
										title: 'Registrar Consumo'
									}
								] : undefined}
							/>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<AddEditPowerGenerationHourMeterModal
				establishment_id={establishment_id}
				dataPowerGenerationHourMeter={dataPowerGenerationHourMeter}
				isEdit={isEdit}
				isOpen={modal}
				handleToggleModal={handleToggleModal}
				activeTab={activeTab}
				toggleTab={toggleTab}
			/>

			<AddEditPowerGenerationConsumptionModal
				establishment_id={establishment_id}
				dataPowerGenerationHourMeter={dataPowerGenerationHourMeter}
				isEdit={isEdit}
				isOpen={modalConsumption}
				handleToggleModal={handleToggleModalConsumption}
				activeTab={activeTab}
				toggleTab={toggleTab}
			/>

			<DeletePowerGenerationHourMeterModal
				dataPowerGenerationHourMeter={dataPowerGenerationHourMeter}
				isOpen={modalDelete}
				handlerToggleModal={handleToggleModalDelete}
			/>
		</React.Fragment>
	);
};

export default PowerGenerationHourMeterList;
